import React, { useState } from 'react';
import {
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  User,
  ListTodo,
  MessageSquare,
  Calendar,
  Ticket,
  Settings,
  Users,
  Plug,
  AlertTriangle,
  UsersRound,
  PhoneCall,
  ChevronDown,
  ChevronRight,
  KanbanSquare,
  Wrench,
  ClipboardList,
  FileBarChart2,
  Workflow,
} from 'lucide-react';
import logo from "../Images/ORCA_SIDEBAR.png";

const Sidebar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  isMobile,
  currentPage,
  handlePageChange,
  handleLogout
}) => {

  const [expandedMenus, setExpandedMenus] = useState({});

  // 🔥 Accordion behavior (one submenu at a time)
  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev => {
      if (prev[menuId]) {
        return {};
      }
      return { [menuId]: true };
    });
  };

  const menuItems = [
    {
      id: 'dashboard',
      icon: LayoutDashboard,
      label: 'Dashboard',
      hasSubmenu: false
    },
    {
      id: 'profile',
      icon: User,
      label: 'Profile',
      hasSubmenu: true,
      submenu: [
        { id: 'ViewProfiles', label: 'View Profile' },
        { id: 'ViewEditProfile', label: 'Edit Profile' }
      ]
    },
    {
      id: 'task',
      icon: ListTodo,
      label: 'Task',
      hasSubmenu: true,
      submenu: [
        { id: 'TaskTicket', label: 'All Tasks' },
        { id: 'TicketPending', label: 'Pending' },
        { id: 'TicketComplete', label: 'Completed' }
      ]
    },
    {
      id: 'AdminMessenger',
      icon: MessageSquare,
      label: 'Chat',
      hasSubmenu: false
    },
    {
      id: 'CalendarApp',
      icon: Calendar,
      label: 'Calendar',
      hasSubmenu: false
    },
    {
      id: 'ticketing',
      icon: Ticket,
      label: 'Ticketing',
      hasSubmenu: true,
      submenu: [
        { id: 'TicketsPage', label: 'New Ticket' },
        { id: 'OpenTickets', label: 'Open Tickets' },
        { id: 'ClosedTickets', label: 'Closed Tickets' }
      ]
    },
    {
      id: 'maintenance',
      icon: Wrench,
      label: 'Maintenance',
      hasSubmenu: false
    },
    {
      id: 'customer',
      icon: Users,
      label: 'Customer',
      hasSubmenu: true,
      submenu: [
        { id: 'CustomerList', label: 'Customer List' },
        { id: 'AddCustomerList', label: 'Add Customer' }
      ]
    },
    {
      id: 'Plugin',
      icon: Plug,
      label: 'Plugins',
      hasSubmenu: false
    },
    {
      id: 'Incident',
      icon: AlertTriangle,
      label: 'Incidents',
      hasSubmenu: false
    },
    {
      id: 'UserAdminManagement',
      icon: UsersRound,
      label: 'Users',
      hasSubmenu: false
    },
    {
      id: 'Oncall',
      icon: PhoneCall,
      label: 'On-call',
      hasSubmenu: false
    },
    {
      id: 'ReviewContact',
      icon: PhoneCall,
      label: 'Contact',
      hasSubmenu: false
    },
    {
      id: 'Workload',
      icon: FileBarChart2,
      label: 'Reports',
      hasSubmenu: false
    },
    {
      id: 'FlowchartBuilder',
      icon: Workflow,
      label: 'Flow Chart Builder',
      hasSubmenu: false
    },
    {
      id: 'KanbanBoard',
      icon: ClipboardList,
      label: 'Create Plan',
      hasSubmenu: true,
      submenu: [
        { id: 'BoardView', label: 'Board View' },
        { id: 'ListView', label: 'List View' },
        { id: 'MyCards', label: 'My Cards' },
        { id: 'Archived', label: 'Archived' },
      ]
    },
  ];

  return (
    <>
      {/* MOBILE OVERLAY */}
      {isMobile && isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 bg-black bg-opacity-60 z-40 transition-opacity backdrop-blur-sm"
        />
      )}

      {/* SIDEBAR */}
      <div className={`
        fixed top-0 left-0 h-full bg-slate-900 text-white shadow-2xl z-50
        transition-all duration-300 ease-in-out flex flex-col
        border-r border-slate-800
        ${isSidebarOpen ? 'w-64' : 'w-20'}
        ${isMobile && !isSidebarOpen ? '-translate-x-full' : 'translate-x-0'}
      `}>

        {/* HEADER */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          {isSidebarOpen && (
            <div className="flex items-center gap-3">

              {/* Logo Container */}
              <div className="w-15 h-15 p-1 bg-white rounded-lg flex items-center justify-center">
                <img
                  src={logo}
                  alt="ORCA Logo"
                  className="w-12 h-12 object-contain"
                />
              </div>


              <span className="font-bold text-lg text-gray-100 whitespace-nowrap">
                Helpdesk
              </span>

            </div>
          )}

          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 hover:bg-slate-800 rounded-lg transition"
          >
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* NAVIGATION */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">

          {menuItems.map((item) => {
            const Icon = item.icon;
            const isExpanded = expandedMenus[item.id];

            const isActive =
              currentPage === item.id ||
              item.submenu?.some(sub => sub.id === currentPage);

            return (
              <div key={item.id}>
                <button
                  onClick={() => {
                    if (item.hasSubmenu && isSidebarOpen) {
                      toggleSubmenu(item.id);
                    } else {
                      handlePageChange(item.id);
                      if (isMobile) setIsSidebarOpen(false);
                    }
                  }}
                  className={`
                    w-full flex items-center justify-between px-3 py-2.5 rounded-lg
                    transition-all duration-200
                    ${isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:bg-slate-800 hover:text-gray-200'}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={20} />
                    {isSidebarOpen && (
                      <span className="text-sm font-medium">
                        {item.label}
                      </span>
                    )}
                  </div>

                  {item.hasSubmenu && isSidebarOpen && (
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  )}
                </button>

                {/* SUBMENU */}
                {item.hasSubmenu && isExpanded && isSidebarOpen && (
                  <div className="mt-1 ml-8 space-y-1">
                    {item.submenu.map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => {
                          handlePageChange(sub.id);
                          if (isMobile) setIsSidebarOpen(false);
                        }}
                        className={`
                          w-full text-left px-3 py-2 rounded-lg text-sm
                          ${currentPage === sub.id
                            ? 'bg-slate-800 text-blue-400'
                            : 'text-gray-500 hover:bg-slate-800 hover:text-gray-300'}
                        `}
                      >
                        <div className="flex items-center gap-2">
                          <ChevronRight size={14} />
                          {sub.label}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* DESKTOP LOGOUT */}
        {!isMobile && (
          <div className="p-3 border-t border-slate-800">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                bg-slate-800 hover:bg-red-600 text-gray-400 hover:text-white transition"
            >
              <LogOut size={20} />
              {isSidebarOpen && <span className="text-sm font-medium">Logout</span>}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;